import { useMemo } from 'react';
import { HistoryCard } from '@/components/history/HistoryCard';
import type { Payment, PolicyInterestRow } from '@/types';
import { formatCurrency, formatPaymentDate, paymentTypeLabel, formatPaymentPeriod } from '@/lib/payments';
import { formatMonthKey } from '@/lib/months';

interface SavingsHistoryProps {
  /** "#27 - Arun Prakash · P2701", shown in the View All modal. */
  subtitle: string;
  interestRows: PolicyInterestRow[];
  payments: Payment[];
}

const formatRupees = (amount: number) => `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;


/** Interest History (monthly 6% interest) and Payment History of a policy, in the shared history layout. */
export function SavingsHistory({ subtitle, interestRows, payments }: SavingsHistoryProps) {
  // Running "Policy Paid" total after each payment, newest first.
  const paymentRows = useMemo(() => {
    const oldestFirst = [...payments].sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));
    let total = 0;
    return oldestFirst
      .map((payment) => {
        total += payment.amount;
        return { payment, policyPaid: total };
      })
      .reverse();
  }, [payments]);

  return (
    <HistoryCard
      title="Policy Savings"
      subtitle={subtitle}
      tabs={[
        {
          id: 'interest',
          label: 'Interest History',
          empty: 'No interest yet. Interest starts the month after the first payment.',
          columns: [
            { label: 'Month' },
            { label: 'Policy Paid' },
            { label: 'Interest (6% / yr)' },
            { label: 'Total Interest' },
            { label: 'Total Value' },
          ],
          rows: interestRows.map((row) => ({
            key: row.month,
            cells: [
              formatMonthKey(row.month),
              formatCurrency(row.policyPaid),
              formatRupees(row.interest),
              formatRupees(row.totalInterest),
              <b key="value">{formatRupees(row.totalValue)}</b>,
            ],
          })),
        },
        {
          id: 'payments',
          label: 'Payment History',
          empty: 'No policy payments yet.',
          columns: [{ label: 'Paid On' }, { label: 'Period' }, { label: 'Mode' }, { label: 'Amount' }, { label: 'Policy Paid' }],
          rows: paymentRows.map(({ payment, policyPaid }) => ({
            key: payment.id,
            cells: [
              formatPaymentDate(payment.paymentDate),
              formatPaymentPeriod(payment),
              <span key="mode" className="text-gray-900">{paymentTypeLabel(payment.paymentType)}</span>,
              formatCurrency(payment.amount),
              <b key="paid">{formatCurrency(policyPaid)}</b>,
            ],
          })),
        },
      ]}
    />
  );
}
